import { Tip } from '~/components/ui/tip';
import { useChatActivity } from '~/features/chat/top-bar/use-chat-activity';
import type { BarChatState } from '~/features/chat/top-bar/state-mapping';
import { cn } from '~/lib/utils';

/**
 * The one status dot for a chat or Build (eddy, 2026-10-26): grey while idle
 * — a brand-new chat included — brand blue while it works, amber while it
 * waits on the person. Used by the chat window's lamp and the top bar's window
 * tabs (instead of a kind icon), so the two can never disagree.
 */
export function lampFor(state: BarChatState | undefined): { label: string; tone: string; pulse: boolean } {
  if (state === 'running') return { label: 'Working', tone: 'bg-[var(--eddy-blue)] ring-[var(--eddy-blue)]/20', pulse: true };
  if (state === 'asking') return { label: 'Waiting for you', tone: 'bg-amber-500 ring-amber-500/20', pulse: false };
  return { label: 'Idle', tone: 'bg-muted-foreground/45 ring-muted-foreground/15', pulse: false };
}

/** Just the dot (no tooltip) — for places that carry their own name, like a tab. */
export function StatusDot({ state, className }: { state: BarChatState | undefined; className?: string }) {
  const lamp = lampFor(state);
  return (
    <span
      data-testid="status-dot"
      data-state={state ?? 'idle'}
      aria-hidden
      className={cn(
        'size-2 shrink-0 rounded-full ring-[3px] transition-[background-color,box-shadow] duration-300',
        lamp.tone,
        lamp.pulse && 'motion-safe:animate-pulse',
        className,
      )}
    />
  );
}

/** The chat window's own lamp, like the Build window's `BuildLamp`. Always shown. */
export function ChatLamp({ sessionId }: { sessionId: string | null }) {
  const { barChats } = useChatActivity({});
  const state = sessionId ? barChats.find((c) => c.sessionId === sessionId)?.state : undefined;
  return <ChatLampView state={state} />;
}

export function ChatLampView({ state }: { state: BarChatState | undefined }) {
  const lamp = lampFor(state);
  return (
    <Tip label={lamp.label}>
      {/* The padded box is the hover target, as on the Build lamp. */}
      <span data-testid="chat-lamp" role="img" aria-label={lamp.label} className="inline-flex shrink-0 cursor-default items-center p-1.5">
        <StatusDot state={state} />
      </span>
    </Tip>
  );
}
